import type { RefObject } from 'preact';
import { useRef, useState } from 'preact/hooks';
import { Calendar as CalendarIcon, X } from 'lucide-preact';
import dayjs from 'dayjs';
import { formatDateRange, formatShortDate } from '@/lib/format';
import {
  DATE_RANGE_PRESETS,
  DATE_RANGE_PRESET_LABELS,
  presetUsesIncludeToday,
  resolvePresetRange,
  type DateRange,
  type DateRangePreset,
} from '@/lib/datePresets';
import { Popover } from '../overlays/Popover';
import { Calendar } from './Calendar';
import { cn } from '@/lib/cn';

export type { DateRange, DateRangePreset } from '@/lib/datePresets';

/** Cómo se eligió el rango: con un atajo (que avanza con el calendario) o a mano (`null`). */
export interface DateRangeChange {
  preset: DateRangePreset | null;
}

export interface DateRangePickerProps {
  value: DateRange;
  /**
   * Solo se llama con un rango COMPLETO (inicio y fin, ordenados) o, al limpiar,
   * con los dos extremos a `null`. El primer clic del calendario no sale de aquí:
   * el selector lo guarda como borrador hasta el segundo.
   */
  onChange: (value: DateRange, change: DateRangeChange) => void;
  /**
   * Atajo activo. Si se pasa (también `null`), manda el consumidor; si no, se
   * marca el atajo cuyas fechas coinciden con `value`.
   */
  activePreset?: DateRangePreset | null;
  /** "Incluir hoy" controlado. Sin él, el selector lo guarda por su cuenta (desmarcado). */
  includeToday?: boolean;
  onIncludeTodayChange?: (includeToday: boolean) => void;
  /** Primer y último día elegibles (p. ej. `maxDate` = hoy en vistas de datos, que no cubren el futuro). */
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  format?: string;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  invalid?: boolean;
  clearable?: boolean;
  presets?: readonly DateRangePreset[];
  className?: string;
}

const TRIGGER_BASE =
  'flex min-w-0 w-full items-center gap-2 rounded-md border bg-surface text-fg border-border-subtle text-left transition-[border-color,box-shadow] duration-med ease-snappy focus-visible:outline-none focus-visible:border-brand focus-visible:shadow-focus disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer';

const SIZE: Record<NonNullable<DateRangePickerProps['size']>, string> = {
  sm: 'h-7 px-2.5 text-xs',
  md: 'h-9 px-3 text-13',
  lg: 'h-10 px-3.5 text-sm',
};

const PRESET_BASE = 'rounded px-2 py-1.5 text-left text-xs';

function sameDay(a: Date | null, b: Date | null): boolean {
  return !!a && !!b && dayjs(a).isSame(b, 'day');
}

function matchingPreset(
  value: DateRange,
  presets: readonly DateRangePreset[],
  includeToday: boolean,
): DateRangePreset | null {
  if (!value.start || !value.end) return null;
  return (
    presets.find((p) => {
      const r = resolvePresetRange(p, includeToday);
      return sameDay(r.start, value.start) && sameDay(r.end, value.end);
    }) ?? null
  );
}

export function DateRangePicker({
  value,
  onChange,
  activePreset: activePresetProp,
  includeToday: includeTodayProp,
  onIncludeTodayChange,
  minDate,
  maxDate,
  placeholder = 'Selecciona un rango',
  format,
  size = 'md',
  disabled,
  invalid,
  clearable = true,
  presets = DATE_RANGE_PRESETS,
  className,
}: DateRangePickerProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  // Primer día elegido en el calendario, a la espera del segundo. Vive aquí y
  // no en el consumidor: antes el selector emitía `{start, end: null}` y cada
  // página lo trataba a su manera (ignorarlo, rellenar el fin con hoy...).
  const [draftStart, setDraftStart] = useState<Date | null>(null);
  const [ownIncludeToday, setOwnIncludeToday] = useState(false);
  const includeToday = includeTodayProp ?? ownIncludeToday;
  const activePreset =
    activePresetProp !== undefined
      ? activePresetProp
      : matchingPreset(value, presets, includeToday);

  // Sin `format` explícito, el botón usa el mismo formateador que el resto de la app
  // (`1 sep → 30 sep`), para que el selector y los textos de la página no discrepen.
  const display = value.start
    ? format
      ? value.end
        ? `${dayjs(value.start).format(format)} → ${dayjs(value.end).format(format)}`
        : `${dayjs(value.start).format(format)} → …`
      : formatDateRange(value.start, value.end)
    : null;

  function close() {
    setOpen(false);
    setDraftStart(null);
  }

  function selectDay(d: Date) {
    if (!draftStart) {
      setDraftStart(d);
      return;
    }
    const [start, end] = d < draftStart ? [d, draftStart] : [draftStart, d];
    onChange({ start, end }, { preset: null });
    close();
  }

  function selectPreset(p: DateRangePreset) {
    onChange(resolvePresetRange(p, includeToday), { preset: p });
    close();
  }

  function toggleIncludeToday(next: boolean) {
    if (includeTodayProp === undefined) setOwnIncludeToday(next);
    onIncludeTodayChange?.(next);
    if (presetUsesIncludeToday(activePreset)) {
      onChange(resolvePresetRange(activePreset, next), { preset: activePreset });
    }
  }

  const showClear = clearable && value.start && !disabled;

  return (
    <>
      {/* El botón de limpiar es un <button> HERMANO del trigger (posicionado
          absoluto), no anidado dentro de él: un control interactivo no puede
          contener otro (axe `nested-interactive`, serious). El className del
          consumidor controla el ancho/margen del control completo → va al
          wrapper. */}
      <div className={cn('relative', className)}>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-invalid={invalid}
          onClick={() => (open ? close() : setOpen(true))}
          className={cn(
            TRIGGER_BASE,
            SIZE[size],
            showClear && 'pr-9',
            invalid && 'border-error focus-visible:border-error focus-visible:shadow-focus-error',
          )}
        >
          <CalendarIcon size={14} className="text-fg-faint shrink-0" />
          <span className={cn('flex-1 truncate', !display && 'text-fg-faint')}>
            {display ?? placeholder}
          </span>
        </button>
        {showClear ? (
          <button
            type="button"
            aria-label="Limpiar rango"
            className="text-fg-faint hover:bg-surface-2 hover:text-fg focus-visible:shadow-focus absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 transition-colors focus-visible:outline-none"
            onClick={(e) => {
              e.stopPropagation();
              onChange({ start: null, end: null }, { preset: null });
              setDraftStart(null);
            }}
          >
            <X size={12} />
          </button>
        ) : null}
      </div>
      <Popover
        open={open}
        onClose={close}
        anchor={triggerRef as RefObject<HTMLElement>}
        placement="bottom-start"
      >
        {/* En móvil los atajos van en fila encima del calendario: lado a lado no
            caben en 390px y el calendario salía cortado. */}
        <div className="flex max-w-[calc(100vw-16px)] flex-col sm:flex-row">
          {presets.length > 0 ? (
            <div className="border-border-subtle flex flex-wrap gap-0.5 border-b p-2 sm:w-32 sm:flex-col sm:flex-nowrap sm:border-r sm:border-b-0">
              {presets.map((p) => {
                const active = p === activePreset;
                return (
                  <button
                    key={p}
                    type="button"
                    aria-pressed={active}
                    onClick={() => selectPreset(p)}
                    className={cn(
                      PRESET_BASE,
                      active
                        ? 'bg-brand-tint text-brand font-medium'
                        : 'text-fg hover:bg-surface-2',
                    )}
                  >
                    {DATE_RANGE_PRESET_LABELS[p]}
                  </button>
                );
              })}
              {presets.some((p) => presetUsesIncludeToday(p)) ? (
                <label className="text-fg hover:bg-surface-2 flex items-center gap-2 rounded px-2 py-1.5 text-xs sm:mt-1">
                  <input
                    type="checkbox"
                    checked={includeToday}
                    onChange={(e) => toggleIncludeToday(e.currentTarget.checked)}
                    className="border-border-subtle accent-brand h-3.5 w-3.5 rounded"
                  />
                  <span>Incluir hoy</span>
                </label>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-col">
            <Calendar
              rangeStart={draftStart ?? value.start}
              rangeEnd={draftStart ? null : value.end}
              onSelect={selectDay}
              min={minDate}
              max={maxDate}
            />
            {draftStart ? (
              <p className="text-fg-muted px-3 pb-2 text-xs" role="status">
                Desde el {formatShortDate(draftStart)}: elige el último día
              </p>
            ) : null}
          </div>
        </div>
      </Popover>
    </>
  );
}
